import React, { FC, useEffect, useMemo, useRef } from 'react';
import { FormText, Input, Popover, PopoverBody, PopoverHeader, Progress } from 'reactstrap';
import zxcvbn, { ZXCVBNScore } from 'zxcvbn';
import { TranslationKey, useTranslation } from '../translations';
import { useBoolState, useFetcher, useInputState } from '../utils';

interface PasswordStrengthProps {
  readonly minLength: number;
  readonly minScore: number;
  readonly onChange?: (value: string) => void;
}

const scoreMap: Record<
  ZXCVBNScore,
  {
    color: string;
    text: TranslationKey;
  }
> = [
  {
    color: 'danger',
    text: 'new_password_input.terrible_password_is_too_insecure',
  },
  {
    color: 'danger',
    text: 'new_password_input.bad_you_can_do_better',
  },
  {
    color: 'warning',
    text: 'new_password_input.fair_enough',
  },
  {
    color: 'info',
    text: 'new_password_input.good',
  },
  {
    color: 'success',
    text: 'new_password_input.great',
  },
];

export const NewPasswordInput: FC<PasswordStrengthProps> = ({ onChange, minLength, minScore }) => {
  const { ts } = useTranslation();

  const [focus, enableFocus, disableFocus] = useBoolState(false);
  // The compiler narrows this to `''` which is incorrect.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-arguments
  const [value, setValue] = useInputState<string>('');
  const passwordInput = useRef<HTMLInputElement>(null);
  const fetcher = useFetcher(async () => {
    return { lib: (await import(/* webpackChunkName: "zxcvbn" */ 'zxcvbn')).default };
  }, []);

  const { color, text, feedback, score } = useMemo(() => {
    const checker: typeof zxcvbn | undefined = fetcher.data?.lib;
    if (value.length < minLength) {
      return {
        color: 'muted',
        score: 0,
        text: 'new_password_input.password_must_be_at_least' as TranslationKey,
      };
    }

    if (!checker) {
      return {
        color: 'muted',
        score: -1,
      };
    }

    const check = checker(value);
    const scoreResult = scoreMap[check.score];

    return {
      color: scoreResult.color,
      text: scoreResult.text,
      feedback: check.feedback,
      score: check.score,
    };
  }, [fetcher.data, value, minLength]);

  useEffect(() => {
    if (onChange) {
      onChange(value);
    }
  }, [onChange, value]);

  useEffect(() => {
    const { current } = passwordInput;
    if (!current) {
      return;
    }

    if (value.length === 0) {
      current.setCustomValidity('');
      return;
    }

    const goodScore = score === -1 || score >= minScore;
    if (value.length > minLength && goodScore) {
      current.setCustomValidity('');
    } else {
      current.setCustomValidity(ts('new_password_input.you_must_choose_a_stronger'));
    }
  }, [value, score, minLength, fetcher.data, minScore, ts]);

  return (
    <>
      <Input
        autoComplete="new-password"
        id="password"
        innerRef={passwordInput}
        name="password"
        onBlur={disableFocus}
        onChange={setValue}
        onFocus={enableFocus}
        type="password"
        value={value}
      />
      <div className="mt-10" id="feedback-container">
        <Progress color={color === 'muted' ? 'danger' : color} value={Math.floor(25 * score)} />
        <FormText color={color}>{text ? ts(text, minLength) : ''}</FormText>
      </div>
      <Popover
        isOpen={
          focus &&
          value !== '' &&
          (!!feedback?.warning || !!feedback?.suggestions.length) &&
          score < 3
        }
        placement="bottom"
        target="feedback-container"
      >
        <PopoverHeader>{ts('new_password_input.password_help')}</PopoverHeader>
        <PopoverBody>
          {feedback?.warning ?? ts('new_password_input.too_short')}
          <ul>
            {feedback?.suggestions.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
        </PopoverBody>
      </Popover>
    </>
  );
};
